import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from './api.js';
import { en, type Dict } from './locales/en.js';
import { es } from './locales/es.js';
import { pt } from './locales/pt.js';

// Lightweight i18n (§6 — labels translated per language). Detection: a saved
// manual choice wins, else the browser language (navigator.language), else en.
// No external service — accurate to the reader's actual language.
export type Lang = 'en' | 'es' | 'pt';
export const LANGS: { code: Lang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
];

const dictionaries: Record<Lang, Dict> = { en, es, pt };
const STORAGE_KEY = 'fiesta_lang';

function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'es' || saved === 'pt') return saved;
  } catch {
    /* localStorage unavailable */
  }
  const nav = (typeof navigator !== 'undefined' && navigator.language) || 'en';
  const code = nav.slice(0, 2).toLowerCase();
  if (code === 'es') return 'es';
  if (code === 'pt') return 'pt';
  return 'en';
}

type TParams = Record<string, string | number>;
interface I18nState {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: keyof Dict, params?: TParams) => string;
  // Translate an error: ApiError.code → err_<CODE> message, falling back to the
  // server message (for codes we don't translate), then a fallback key.
  tError: (e: unknown, fallback?: keyof Dict) => string;
}

const I18nContext = createContext<I18nState | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key: keyof Dict, params?: TParams): string => {
      const template = dictionaries[lang][key] ?? dictionaries.en[key] ?? String(key);
      if (!params) return template;
      return template.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? `{${k}}`));
    },
    [lang],
  );

  const tError = useCallback(
    (e: unknown, fallback?: keyof Dict): string => {
      const d = dictionaries[lang] as Record<string, string>;
      const enD = en as Record<string, string>;
      if (e instanceof ApiError) {
        if (e.code) {
          const msg = d[`err_${e.code}`] ?? enD[`err_${e.code}`];
          if (msg) return msg;
        }
        // Unknown code → the server's own message is more specific than generic.
        if (e.message) return e.message;
      }
      return d[fallback ?? 'err_generic'] ?? enD[fallback ?? 'err_generic'] ?? en.err_generic;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t, tError }), [lang, setLang, t, tError]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nState {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

// Convenience: just the translate function.
export function useT(): I18nState['t'] {
  return useI18n().t;
}
